import { useState } from 'react';
import { BrightcovePlayer, NextgenImage, LoaderType, useBreakpoint } from '@nextgen-web-framework/all';
import { ConfigService } from '@nextgen-web-framework/core';
import { Video } from './types/propsTypes';
import type VideoSectionProps from './types/propsTypes';
import styles from './VideoSection.module.scss';

const VideoSection = (props: VideoSectionProps) => {
  const configs = ConfigService.getConfigs();
  const [isPlaying, setIsPlaying] = useState(false);
  const viewPortType = useBreakpoint();
  const thumbnail = props?.images?.find((item) => item?.viewport?.includes(viewPortType));

  const handlePlay = () => {
    setIsPlaying(true);
  };

  const handleKeyDown = (event: React.KeyboardEvent) => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      handlePlay();
    }
  };

  const renderYoutube = () => {
    const autoplayUrl = `${props.videoUrl}${isPlaying || props.autoPlay ? '?autoplay=1&mute=1&playsinline=1' : ''}`;

    return (
      <iframe
        className={styles['video-player']}
        src={autoplayUrl}
        title={props.title}
        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
        allowFullScreen
      ></iframe>
    );
  };

  const renderVideoUpload = () => (
    <video className={styles['video-player']} controls autoPlay={isPlaying || props.autoPlay}>
      <track kind="captions" />
      <source src={props.videoUrl} />
    </video>
  );

  const renderBrightcove = () => (
    <div className={styles['video-player']}>
      <BrightcovePlayer
        isPlayerResponsive={false}
        containerClassName={`${styles['modal-brightCoveContainer']}`}
        accountId={props.brightcove?.accountId ?? (configs.NEXT_BRIGHT_COVE_ACC_ID as string)}
        playerId={props.brightcove?.playerId ?? (configs.NEXT_BRIGHT_COVE_PLAYER_ID as string)}
        videoId={props.brightcove?.videoId ?? ''}
        poster={props.brightcove?.poster}
        autoplay={isPlaying || props.autoPlay}
        muted
      />
    </div>
  );

  const renderVideoPlayer = () => {
    switch (props.videoType) {
      case Video.brightcove:
        return renderBrightcove();
      case Video.youtube:
        return renderYoutube();
      case Video.upload:
        return renderVideoUpload();
      default:
        return <></>;
    }
  };

  return (
    <div className={styles['video-section-container']} data-testid="video-section-testid">
      {!isPlaying && thumbnail?.url && !props.autoPlay ? (
        <button
          type="button"
          key={thumbnail.url}
          data-testid="video-section-thumbnail-container-testId"
          className={styles['video-section-thumbnail-container']}
          onClick={handlePlay}
          onKeyDown={handleKeyDown}
          aria-label="Play video thumbnail"
        >
          <NextgenImage
            className={styles['video-section-thumbnail-image']}
            src={thumbnail.url}
            alt={thumbnail.altText}
            loaderType={LoaderType.CONTENTSTACK}
          />
          <span className={styles['video-section-thumbnail-play-button']}>▶</span>
        </button>
      ) : (
        <div className={styles['video-player-container']}>{renderVideoPlayer()}</div>
      )}
    </div>
  );
};

export default VideoSection;
export { VideoSectionProps };
